export interface DeviceHistoryAndScheduleProps {
  deviceId: string;
  data?: any;
  loading?: boolean;
}

export interface AssetHistoryEvent {
  id: number;
  event_type_id: number;
  notes: string;
  additional_details: any;
  source_system: string;
  created_by: string;
  created_date: string;
  record_status: number;
  updated_date: string | null;
  updated_by: string | null;
  event_type_name: string;
  file_metadata?: {
    file_id: number;
    file_name: string;
    blob_name: string;
    container_name: string;
    uploaded_by: string;
    upload_date: string | null;
  };
}

export interface AssetHistoryResponse {
  total_count: number;
  results: Array<{
    serial_number: string;
    base_model: string;
    history_events: AssetHistoryEvent[];
  }>;
}

export interface ProcessedEvent {
  id: string;
  title: string;
  date: string | null;
  type: string;
  details?: any;
  isManual?: boolean;
  documents?: SNDocument[];
}

export interface SNDocument {
  document_title: string;
  stream_url: string;
  document_date: string;
  document_format: string;
  document_type: string;
  sub_document_type?: string;
  category: string;
}

export interface TimelineEventType {
  id: number;
  event_name: string;
  isVisible: boolean;
}

export interface LovTimelineResponse {
  data: TimelineEventType[];
  count: number;
}

export interface EventModalProps {
  open: boolean;
  mode?: "add" | "edit" | "delete";
  event?: {
    id: number | string;
    title: string;
    date: string;
    type: string;
    typeOther?: string;
    details?: {
      createdBy?: string;
      fileName?: string;
      [key: string]: any;
    };
  };
  currentUser?: string;
  eventTypes?: TimelineEventType[];
  onClose: () => void;
  onSave?: (newEvent: any) => void;
  onUpdate?: (updatedEvent: any) => void;
  onDelete?: (id: number | string) => void;
}

// Single source of truth for all timeline event configurations.
// To add, rename, or reclassify any event type — update ONLY this object.
// isSystem: true  → auto-generated by backend, cannot be edited/deleted by user
// isSystem: false → user-created via LOV API, supports add / edit / delete
export const TIMELINE_EVENT_CONFIG = {
  // ── System events (backend-generated) ──────────────────────────────────────
  "Shipping Details":  { displayName: "Shipping",                       isSystem: true  },
  "Warranty Details":  { displayName: "Warranty",                       isSystem: true  },
  "Support Details":   { displayName: "Support",                        isSystem: true  },
  "Service Requested": { displayName: "Service Requested",              isSystem: true  },
  "Device Added":      { displayName: "Device Added to Turnaround",     isSystem: true  },
  "Device Removed":    { displayName: "Device Removed from Turnaround", isSystem: true  },
  "Walkdown":          { displayName: "Walkdown",                       isSystem: true  },
  "Device Created":    { displayName: "",                               isSystem: true  }, // internal only — no card shown
  "Device Updated":    { displayName: "",                               isSystem: true  }, // internal only — no card shown

  // ── Frontend-synthesized events (produced by processDocumentsToSNEvents) ───
  "Factory docs":      { displayName: "Serial Specific Documents",                   isSystem: true  }, // SNDocs from /documents-and-drawings

  // ── Manual events (user-created via LOV API) ────────────────────────────────
  "Calibration":           { displayName: "Calibration / Validation / Verification", isSystem: false },
  "Open/Inspect":          { displayName: "Open / Inspect",                          isSystem: false },
  "Diagnostics":           { displayName: "Perform Diagnostics",                     isSystem: false },
  "Repair Booked":         { displayName: "Repair",                                  isSystem: false },
  "Replace":               { displayName: "Replace",                                 isSystem: false },
  "Startup/Commissioning": { displayName: "Start Up & Commission",                   isSystem: false },
  "Report":                { displayName: "Report",                                  isSystem: false },
} as const satisfies Record<string, { displayName: string; isSystem: boolean }>;

// Derived from TIMELINE_EVENT_CONFIG — display name strings are defined only once above.
// If you rename a display name in the config, it automatically propagates here and everywhere
// TimelineEventTypeEnum is used in the component.
//
// "Turnaround" is the one exception: it is a UI-only grouping label used in filters and table
// headers to represent both DeviceAdded and DeviceRemoved together. No API event ever produces
// type "Turnaround" directly — it is never stored on a ProcessedEvent.
export const TimelineEventTypeEnum = {
  ServiceRequested:      TIMELINE_EVENT_CONFIG["Service Requested"].displayName,
  Report:                TIMELINE_EVENT_CONFIG["Report"].displayName,
  Shipping:              TIMELINE_EVENT_CONFIG["Shipping Details"].displayName,
  Warranty:              TIMELINE_EVENT_CONFIG["Warranty Details"].displayName,
  Support:               TIMELINE_EVENT_CONFIG["Support Details"].displayName,
  Turnaround:            "Turnaround" as const, // UI-only grouping — groups DeviceAdded + DeviceRemoved
  DeviceAdded:           TIMELINE_EVENT_CONFIG["Device Added"].displayName,
  DeviceRemoved:         TIMELINE_EVENT_CONFIG["Device Removed"].displayName,
  FactoryDocs:           TIMELINE_EVENT_CONFIG["Factory docs"].displayName,
  DeviceLinkedToWalkdown: TIMELINE_EVENT_CONFIG["Walkdown"].displayName,
} as const;

// Safe runtime lookup for TIMELINE_EVENT_CONFIG by a dynamic string key.
// Use this instead of direct indexing to avoid TS7053 (no index signature on 'as const' type).
export const getEventConfig = (name: string): { displayName: string; isSystem: boolean } | undefined =>
  (TIMELINE_EVENT_CONFIG as Record<string, { displayName: string; isSystem: boolean }>)[name];

// Derived: manual-event name map — used in getEventTypes() to remap raw LOV names
export const EventVerbiageMap: Record<string, string> = Object.fromEntries(
  Object.entries(TIMELINE_EVENT_CONFIG)
    .filter(([, v]) => !v.isSystem)
    .map(([k, v]) => [k, v.displayName])
);

// Constants
export const MAX_FILE_SIZE_MB = 20;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
